import React, {Dispatch, useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import classNames from 'classnames';
import {
  Alert,
  Button,
  DeviceIcon,
  FacebookIcon,
  FlickrIcon,
  GooglePhotosIcon,
  InstagramIcon,
  PictureIcon,
  UploadIcon,
  UserIcon,
  ButtonWithPopout,
  AddPicturesIcon,
  DialogHeader,
} from '@taopix/taopix-design-system';
import AssetServiceDialog from './AssetServiceDialog';
import {AssetPart, AssetStatus, Common, CoreActions} from '@taopix/taopix-core';
import {ActionType, AssetMetaInterface, AssetService} from '@taopix/taopix-core/dist/types/types';
import {useTaopix} from '../Context/TaopixContext';
import {useUploader} from '../Context/Upload/UploadContext';
import {CoreAsset, ExternalAsset} from '@taopix/taopix-asset-manager/dist/types/types';
import AssetContext from '@taopix/taopix-asset-manager/dist/types/Context/AssetContext';
import {COLOUR_COMPONENT_FLATTENED, ROUTE_ASSISTANT} from '../../Constants';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import {UserInterfaceActions} from '../Context/UserInterface/UserInterfaceActionTypes';
import {useExperience} from '../Experience/ExperienceProvider';
import useSettings from '../../Hooks/useSettings';
import {AssetServicesSelector, LayoutSelector, UserAssetSelector} from '../../Selectors';
import {AssetService as UploadAssetService} from '@taopix/taopix-asset-manager/dist/types/UploadServiceProvider';
import {useUserFlowState} from '../Context/UserFlow/UserFlowContext';
import {UserFlowType} from '../../Facade/UserFlow/UserFlowFacade';
import {UserFeedbackActions} from '../Context/UserFeedback/UserFeedbackActionTypes';
import {useUserFeedback} from '../Context/UserFeedback/UserFeedbackContext';
import {PopOutProps} from '@taopix/taopix-design-system/dist/types/Components/PopOut/PopOut';
import {ButtonProps} from '@taopix/taopix-design-system/dist/types/Components/Button/Button';

export type PictureUsage = {
  [key: number]: number;
};

export enum PictureSortType {
  DateTaken = 'datetimeoriginal',
  Filename = 'name',
  Size = 'fileSizeBytes',
  Orientation = 'orientation',
  Project = 'project',
}

export enum PictureAction {
  RotateSelected,
  RemoveSelected,
  RemoveAll,
}

export type AddPicturesButtonProps = {
  selectedPictureAction?: Function;
  close?: () => void;
  onLoading?: (truthy: boolean) => void;
  onFilesAdded?: () => void;
  popoutUsesParentWidth?: boolean;
  buttonProps?: Partial<ButtonProps>;
  popoutProps?: Partial<PopOutProps>;
};

const AddPicturesButton = ({
  selectedPictureAction,
  close,
  onLoading,
  onFilesAdded,
  popoutUsesParentWidth,
  buttonProps,
  popoutProps,
  ...props
}: AddPicturesButtonProps) => {
  const app = useTaopix();
  const settings = useSettings();
  const ui = useUiState();
  const uploader = useUploader();
  const userFlow = useUserFlowState();
  const {experience} = useExperience();
  const userFeedback = useUserFeedback();

  // grab the list of user asset from the store
  const assets = useSelector(UserAssetSelector);

  // get the layout from the store
  const productLayout = useSelector(LayoutSelector);

  // create an action dispatcher for the core
  const dispatch = useDispatch<Dispatch<ActionType<typeof CoreActions>>>();

  // pull in the assetServices
  const assetServices = useSelector(AssetServicesSelector);

  // pull in language support
  const {t} = useTranslation(['*', 'main']);
  const stateManager = app.core.get('stateManager');
  const server = app.core.get('server');

  const [error, setError] = useState({
    visible: false,
    message: '',
  });

  const [assetServiceState, setAssetServiceState] = useState({
    selectedServiceID: -1,
    serviceDialogOpen: false,
    open: false,
  });

  // loop through the project image list and queue any unprocessed assets
  useEffect(() => {
    if (!Object(uploader).hasOwnProperty('get')) {
      return;
    }

    if (!assetServices.length) {
      return;
    }

    const unqueued = assets.filter(
      a =>
        a.serviceCode === '%TAOPIXONLINE%' &&
        a.status === AssetStatus.NotQueued &&
        !Object.prototype.hasOwnProperty.call(a, 'uploadId')
    );

    if (!unqueued.length) {
      return;
    }

    // get the default asset service
    const defaultService = assetServices.find(
      s => s.code === '%TAOPIXONLINE%' || s.code === '%UPLOADFROMDEVICE%'
    ) as AssetService;

    const processor = uploader.get(defaultService as UploadAssetService);

    processor.on('queued', event => {
      const {entry} = event.detail;
      // the page state manager gives us an easy way of fetching an asset from the store
      const asset = stateManager.getAsset(entry.getState().id as number);
      if (!asset) {
        return;
      }
      // update the asset
      stateManager.updateAsset({
        ...asset,
        status: AssetStatus.Uploaded,
      });
    });

    processor.on('update', event => {
      assetsUpdateHandler(event);
    });

    processor.on('error', event => assetErrorHandler(event));

    // add the unprocessed assets to the processor
    unqueued.forEach(asset => processor.add(asset));
  }, [uploader]);

  /**
   * This maps the TAOPIX asset service codes
   * to their experience values
   */
  const permissions = useMemo(
    () => ({
      '%TAOPIXONLINE%': experience.SETTINGS.standardAssetServices.upload,
      '%PREVIOUSUPLOADS%': experience.SETTINGS.standardAssetServices.previousUploads,
      '%UPLOADFROMDEVICE%': experience.SETTINGS.standardAssetServices.uploadFromPhone,
      '%FACEBOOK%': experience.SETTINGS.externalAssetServices.facebook,
      '%FLICKR%': experience.SETTINGS.externalAssetServices.flickr,
      '%GOOGLEPHOTOS%': experience.SETTINGS.externalAssetServices.googlePhotos,
      '%INSTAGRAM%': experience.SETTINGS.externalAssetServices.instagram,
    }),
    [experience, ui.state.isSmallScreen]
  );

  /**
   * Filter function to check if a service is enabled
   * in the experience settings
   *
   * @param service
   * @returns boolean
   */
  const isEnabled = (service: AssetService): boolean => {
    if (service?.customservice) {
      return experience.SETTINGS.externalAssetServices.libraryLink;
    }
    return permissions[service.code] || false;
  };

  const onOpenAssetServiceDialog = (assetService: AssetService) => {
    if (close) close();
    setAssetServiceState(state => ({
      ...state,
      selectedServiceID: assetService.id,
      serviceDialogOpen: true,
    }));
  };

  const onCloseAssetServiceDialog = () => {
    setAssetServiceState(state => ({
      ...state,
      serviceDialogOpen: false,
    }));
    if (close) close();
  };

  const afterClose = () => {
    // Wait until the dialog's closing transition has completed before resetting the Asset Service ID, or it affects the visible dialog contents
    setAssetServiceState(state => ({
      ...state,
      selectedServiceID: -1,
      serviceDialogOpen: false,
    }));
  };

  /**
   * Handler for when an asset(s) is added/uploaded to the project
   *
   * @param payload
   */
  const assetsAddedHandler = (assets: AssetContext[]) => {
    // add the assets to the state
    dispatch(
      CoreActions.addAssets(
        assets.filter(a => !a.error).map(a => a.getState() as unknown as AssetMetaInterface)
      )
    );

    // close the list
    setAssetServiceState(state => ({
      ...state,
      open: false,
    }));

    // open the pictures panel unless we are on the calendar cell picture palette
    // and not in the fullscreen assistant.
    if (
      !ui.state.palette.section?.includes('calendar-palette') &&
      !ui.state.palette.section?.includes('pictures') &&
      !(
        `#/${ROUTE_ASSISTANT}/` === settings.state.project.stage ||
        window.location.hash.includes(ROUTE_ASSISTANT) ||
        userFlow.isUserFlowActive(UserFlowType.Assistant)
      )
    ) {
      ui.dispatch(UserInterfaceActions.setToolPalette({section: 'pictures'}));
    }
  };

  /**
   * Handles updates from the StatusChecker & updates the store
   *
   * @param event
   */
  const assetsUpdateHandler = (event: CustomEvent<Partial<CoreAsset>[]>) => {
    const updates = event.detail;

    // update each of the assets
    updates.forEach(update => {
      // the page state manager gives us an easy way of fetching an asset from the store
      const asset = stateManager.getAsset(update.id as number);
      if (!asset) {
        return;
      }

      // if this is an AI update, apply the meta data change
      if (update?.metaData) {
        const {status, regions} = update.metaData as any;
        // this is an AI update
        return stateManager.updateAsset({
          ...asset,
          metaData: {
            ...asset.metaData,
            status: status,
            regions: (regions as unknown as string).split(/\r/).filter(String) || [],
          } as any,
        });
      }

      // get a flattened FPO to use for the thumbnail
      const thumbsrc =
        update?.children?.find(
          c =>
            c &&
            c?.part === AssetPart.Intermediate &&
            c?.colourComponent === COLOUR_COMPONENT_FLATTENED
        )?.src || '';

      if ('' !== asset.blobUrl) {
        // eslint-disable-next-line node/no-unsupported-features/node-builtins
        URL.revokeObjectURL(asset.blobUrl);
      }

      // update the asset
      stateManager.updateAsset({
        ...asset,
        ...(update as unknown as AssetMetaInterface),
        src: thumbsrc,
        thumbnailsrc: thumbsrc,
        hiressrc: thumbsrc,
        blobUrl: '',
      });
    });

    // close the busy status (This is here in case the ui cannot generate thumbnails)
    if (onLoading) {
      onLoading(false);
    }
  };

  /**
   * Handles file processing errors
   *
   * @param event
   */
  const assetErrorHandler = (event): void => {
    const {entry, error} = event.detail;

    if (onLoading) {
      onLoading(false);
    }

    // if there is no entry, do nothing
    if (!entry) {
      return;
    }

    // if there is no state, return
    if (!entry?.getState()) {
      return;
    }

    // if the asset has failed before receiving an id, do nothing
    if (!Object.prototype.hasOwnProperty.call(entry?.getState(), 'id')) {
      return;
    }

    // search for the asset which has failed
    const asset = stateManager.getAsset(entry?.getState().id as number);

    // if the error has occurred at validation, there will be no asset record...
    if (!asset) {
      return;
    }

    // otherwise update the asset record with the error
    stateManager.updateAsset({
      ...asset,
      ...(entry as unknown as AssetMetaInterface),
      status: AssetStatus.Error,
      errorCode: error,
    });
  };

  /**
   * Click handler for the asset services
   *
   * @param event
   * @param service
   */
  const onServiceClick = (event: React.MouseEvent, service: AssetService): void => {
    // close the list
    if (close) close();

    if (
      [
        '%TAOPIXONLINE%',
        '%UPLOADFROMDEVICE%',
        '%FACEBOOK%',
        '%FLICKR%',
        '%GOOGLEPHOTOS%',
        '%INSTAGRAM%',
      ].includes(service.code) ||
      service.customservice
    ) {
      // check for active image servers and volumes
      if (
        0 ===
        settings.state.imageServers.servers
          .filter(server => server.active)
          .map(server => {
            // does this server have any active image volumes
            return (
              settings.state.imageServers.volumes.filter(
                volume => volume.serverId === server.id && volume.active
              ).length > 0
            );
          })
          .filter(truthy => truthy).length
      ) {
        return setError({
          visible: true,
          message: '*:str_MessageNoActiveImageServers',
        });
      }
    }

    // if we are not uploading, open the selected service
    if (service.code !== '%TAOPIXONLINE%') {
      onOpenAssetServiceDialog(service);
      return void 0;
    }

    // if the selected service is the default upload from pc
    onOsUpload(service);
  };

  /**
   * Handles files added from the end users PC
   *
   * @param service
   */
  const onOsUpload = useCallback(
    (service: AssetService): void => {
      // open the default file uploader (opens the OS file browser)
      const processor = uploader.get(service as UploadAssetService);
      if (!processor) {
        throw new Error('unable to locate a processor for the requested service');
      }

      // trigger the upload & throw up the busy dialog
      processor.open().then(payload => {
        const {assets} = payload;
        const errors = assets.filter(context => context.error);
        if (errors.length > 0) {
          userFeedback.dispatch(
            UserFeedbackActions.setInfoUserFeedback({
              title: '',
              message:
                1 === errors.length
                  ? t('*:str_WarningImagesTypeNotAllowedSingle')
                  : t('*:str_WarningImagesTypeNotAllowedPlural'),
              showHeader: true,
            })
          );
        }

        // if we have successful files
        if (errors.length !== assets.length) {
          if (onLoading) {
            onLoading(true);
          }
        }
      });

      // subscribe to the events
      processor.on('total', event => {
        if (typeof onFilesAdded === 'function') {
          onFilesAdded();
        }
      });
      processor.on('chunk', event => assetsAddedHandler(event.detail.chunk));
      processor.on('update', event => assetsUpdateHandler(event));
      processor.on('error', event => assetErrorHandler(event));
      processor.on('created', event => {
        // as soon as any thumbnail is rendered, set the busy status to false
        if (onLoading) {
          onLoading(false);
        }
      });
      processor.on('thumbnail', event => {
        const {entry} = event.detail;
        const asset = stateManager.getAsset(entry.getState().id as number);
        if (!asset) {
          return;
        }
        stateManager.updateAsset({
          ...asset,
          thumbnailsrc: entry.getState().thumbnailsrc as string,
          src: entry.getState().thumbnailsrc as string,
          hiressrc: entry.getState().thumbnailsrc as string,
        });
      });
    },
    [uploader, ui.state.palette.section]
  );

  /**
   * Handles the addition of assets from a non standard service (facebook etc)
   * Also handles the addition of files from the users previous uploads
   *
   * @param assets
   * @returns
   */
  const onAddAssets = (assets: ExternalAsset[]): void => {
    const {selectedServiceID} = assetServiceState;
    if (selectedServiceID < 0) {
      return void 0;
    }

    const service = assetServices.find(s => s.id === selectedServiceID);
    if (!service) {
      throw new Error('unknown service id');
    }

    const payload = assets.map(asset => {
      return {
        ...asset,
        serviceCode: service.code,
      };
    });

    const processor = uploader.get(service as UploadAssetService);
    processor.on('total', event => {
      if (typeof onFilesAdded === 'function') {
        onFilesAdded();
      }
    });
    processor.open(payload).then(data => assetsAddedHandler(data.assets as AssetContext[]));
    processor.on('update', event => assetsUpdateHandler(event));
    processor.on('error', event => assetErrorHandler(event));
  };

  const getIcon = (code: string) => {
    switch (code) {
      case '%GOOGLEPHOTOS%':
        return <GooglePhotosIcon />;
      case '%FACEBOOK%':
        return <FacebookIcon />;
      case '%INSTAGRAM%':
        return <InstagramIcon />;
      case '%FLICKR%':
        return <FlickrIcon />;
      case '%TAOPIXONLINE%':
        return <UploadIcon />;
      case '%PREVIOUSUPLOADS%':
        return <UserIcon />;
      case '%UPLOADFROMDEVICE%':
        return <DeviceIcon />;
      default:
        return <PictureIcon />;
    }
  };

  let isExternal = false;
  let separator = <hr className={'border-themeTextColour opacity-20'} />;

  const popoutClassNames = classNames(
    'flex-col',
    popoutUsesParentWidth && 'lg:w-[var(--anchorElementWidth)]',
    popoutProps?.className
  );

  const buttonClassNames = classNames('offsetFocus', buttonProps?.className);

  const getServiceName = useCallback(
    (name: string | string[]): string => {
      if (Array.isArray(name)) {
        if (name[Number(ui.state.isSmallScreen)].startsWith('str_')) {
          return t(name[Number(ui.state.isSmallScreen)], {ns: 'main'});
        }
      }
      return Common.getCurrentLocaleString(name as string, true);
    },
    [ui.state.isSmallScreen]
  );

  return (
    <>
      <ButtonWithPopout
        type="menu"
        buttonProps={{
          label: t('str_ButtonAddPictures'),
          buttonStyle: 'primary',
          startIcon: <AddPicturesIcon />,
          arrow: ui.state.palette.section !== null ? 'down' : undefined,
          ...buttonProps,
          className: buttonClassNames,
        }}
        popoutProps={{
          repositionOnResize: true,
          displayMode: ui.state.isSmallScreen ? 'modal' : 'positioned',
          ...popoutProps,
          className: popoutClassNames,
          'aria-label': t('str_ButtonAddPictures'),
        }}
      >
        {ui.state.isSmallScreen && <DialogHeader>{t('*:str_TitleAddPictures')}</DialogHeader>}

        {assetServices.filter(isEnabled).map(assetService => {
          const icon = getIcon(assetService.code);
          if (!isExternal && assetService.id > 0) {
            separator = <hr className={'my-xs mx-md border-themeTextColour opacity-20'} />;
            isExternal = true;
          } else {
            separator = <></>;
          }
          return (
            <React.Fragment key={assetService.id}>
              {separator}
              <Button
                label={getServiceName(assetService.name)}
                corners={'square'}
                labelAlignment={'left'}
                buttonStyle={'standard'}
                startIcon={icon}
                onClick={e => onServiceClick(e, assetService)}
                id={'assetServiceButton-' + assetService.id}
              />
            </React.Fragment>
          );
        })}

        {ui.state.isSmallScreen && (
          <Button
            label={t('*:str_ButtonClose')}
            buttonStyle="negative"
            onClick={close}
            className={'m-sm'}
          />
        )}
      </ButtonWithPopout>

      <AssetServiceDialog
        designerSettings={settings.state}
        onAddAssets={onAddAssets}
        isOpen={assetServiceState.serviceDialogOpen}
        closeEvent={onCloseAssetServiceDialog}
        assetServices={assetServices}
        serviceId={assetServiceState.selectedServiceID}
        projectImageList={assets}
        pictureUsage={Array.isArray(productLayout?.usagepicture) ? {} : productLayout?.usagepicture}
        afterClose={afterClose}
        server={server}
      />

      <Alert
        id={'asset-service-list'}
        open={error.visible}
        type={'info'}
        message={t(error.message)}
        onClose={() =>
          setError(current => {
            return {
              ...current,
              visible: false,
            };
          })
        }
      />
    </>
  );
};

export default AddPicturesButton;
